import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Modal,
  Space,
  Spin,
  Table,
  Tooltip,
  message,
} from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { hasAbsAccess } from "util/entitlements";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import AbsLockPage from "./AbsLockPage";
import ExplorePanel from "./ExplorePanel";
import OrganizationContextPanel from "./OrganizationContextPanel";
import SegmentBuilder, { ruleAstToDraft } from "./SegmentBuilder";
import SegmentLibrary, { displaySegmentName } from "./SegmentLibrary";
import { ListFilter, Layers, Archive, Compass } from "lucide-react";
import {
  archiveSegment,
  createSegment,
  discoverSegments,
  getAbsInsights,
  getAbsSettings,
  getAnalysis,
  listSegmentMembers,
  listSegments,
  restoreSegment,
  rerunAnalysis,
  uploadAnalysisFile,
} from "./service";
import styles from "./style.module.scss";

const DEFAULT_SOURCES = { voc: true, app: true, web: true, winform: true };
const isAiOrigin = (origin) =>
  origin === "discovered" || origin === "ai_discovered";
const MEMBER_CSV_HEADERS = [
  "email",
  "display_name",
  "product_group",
  "nps",
  "sentiment",
  "last_event_at",
  "event_count",
];

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const membersToCsv = (rows) =>
  [
    MEMBER_CSV_HEADERS.join(","),
    ...rows.map((row) =>
      MEMBER_CSV_HEADERS.map((key) => csvEscape(row[key])).join(",")
    ),
  ].join("\n");

const downloadCsv = (filename, text) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const csvFileName = (name) => {
  const slug = displaySegmentName(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${slug || "audience"}-members.csv`;
};

const entitlementsReady = (subscriptionStatus) =>
  subscriptionStatus?.PackageId != null ||
  subscriptionStatus?.PackageName != null ||
  Array.isArray(subscriptionStatus?.entitlements);

const BehavioralSegmentation = () => {
  const { t } = useTranslation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme) || "light";
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [forcedLock, setForcedLock] = useState(false);
  const [items, setItems] = useState([]);
  const [insights, setInsights] = useState(null);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersTruncated, setMembersTruncated] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);
  const [exportingId, setExportingId] = useState(null);
  const [activeTab, setActiveTab] = useState("explore");
  const [builderDraft, setBuilderDraft] = useState(null);
  const testMode = true;
  const [archivedItems, setArchivedItems] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [uploadingAnalysis, setUploadingAnalysis] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [savingSuggestionKey, setSavingSuggestionKey] = useState(null);

  const applyForbidden = (res) => {
    if (res?.error_code === "ABS_ENTITLEMENT_REQUIRED") {
      setForcedLock(true);
      return true;
    }
    return false;
  };

  const load = useCallback(async (isTest = testMode) => {
    setLoading(true);
    const [segmentsRes, archivedRes, insightsRes, settingsRes, analysisRes] =
      await Promise.all([
        listSegments(isTest),
        listSegments(isTest, true),
        getAbsInsights(isTest),
        getAbsSettings(),
        getAnalysis(),
      ]);
    setLoading(false);
    if (
      applyForbidden(segmentsRes) ||
      applyForbidden(insightsRes) ||
      applyForbidden(archivedRes) ||
      applyForbidden(analysisRes)
    ) {
      return;
    }
    if (!segmentsRes || segmentsRes.error || segmentsRes.database_ready === false) {
      message.error(
        segmentsRes?.error || t("abs.load_error", "Could not load segments")
      );
      setItems([]);
    } else {
      setItems(segmentsRes.items || []);
    }
    if (!archivedRes || archivedRes.error || archivedRes.database_ready === false) {
      setArchivedItems([]);
    } else {
      setArchivedItems(archivedRes.items || []);
    }
    if (insightsRes && !insightsRes.error && insightsRes.database_ready !== false) {
      setInsights(insightsRes);
      if (insightsRes.sources) setSources({ ...DEFAULT_SOURCES, ...insightsRes.sources });
    }
    if (settingsRes && !settingsRes.error && settingsRes.sources) {
      setSources({ ...DEFAULT_SOURCES, ...settingsRes.sources });
    }
    if (analysisRes && !analysisRes.error && analysisRes.database_ready !== false) {
      setAnalysis(analysisRes);
    }
  }, [t, testMode]);

  useEffect(() => {
    if (entitlementsReady(subscriptionStatus) && hasAbsAccess(subscriptionStatus)) {
      load();
    }
  }, [load, subscriptionStatus]);

  useEffect(() => {
    const jobStatus = analysis?.discovery_job?.status;
    if (jobStatus !== "pending" && jobStatus !== "running") return undefined;
    const timer = setInterval(async () => {
      const live = await getAnalysis();
      if (!live || live.error || live.database_ready === false) return;
      setAnalysis(live);
    }, 1500);
    return () => clearInterval(timer);
  }, [analysis?.discovery_job?.status, analysis?.discovery_job?.run_id]);

  const wipeFileAnalysis = async (doneKey) => {
    const res = await rerunAnalysis();
    if (applyForbidden(res)) return;
    if (!res || res.error || res.database_ready === false) {
      message.error(
        res?.error || t("abs.explore_rerun_error", "Could not reset this analysis")
      );
      return;
    }
    setItems([]);
    setArchivedItems([]);
    setInsights(null);
    setAnalysis(res.status ? res : null);
    message.success(t(doneKey, "File cleared"));
    await load();
  };

  const onClearTestData = () => {
    Modal.confirm({
      title: t("abs.test_clear_title", "Start over?"),
      content: t(
        "abs.test_clear_body",
        "This deletes the uploaded events, Explore results, and any audiences saved from this file. Upload the file again to run discovery as a first-time analysis. This cannot be undone."
      ),
      okText: t("abs.test_clear_confirm", "Start over"),
      okButtonProps: { danger: true },
      onOk: () => wipeFileAnalysis("abs.test_cleared"),
    });
  };

  const onRunFromScratch = () => {
    Modal.confirm({
      title: t("abs.explore_rerun_title", "Run from scratch?"),
      content: t(
        "abs.explore_rerun_body",
        "This deletes events, discovered audiences, slice charts, the file name, the identity-filter note, and saved audiences from this file. Memory from the last run is also cleared. Upload the same file again to see a first-time analysis. This cannot be undone."
      ),
      okText: t("abs.explore_rerun_ok", "Run from scratch"),
      okButtonProps: { danger: true },
      onOk: () => wipeFileAnalysis("abs.explore_rerun_done"),
    });
  };

  const executeDiscover = async (isTest) => {
    setDiscovering(true);
    const res = await discoverSegments(sources, isTest);
    setDiscovering(false);
    if (applyForbidden(res)) return;
    await load();
    if (!res || res.error || res.database_ready === false) {
      message.error(
        res?.error || t("abs.discover_error", "Could not discover audiences")
      );
      return;
    }
    if (!res.profile_count) {
      message.info(
        isTest
          ? t(
              "abs.discover_need_test_events",
              "No test events yet. Upload a CSV to discover audiences."
            )
          : t(
              "abs.discover_need_events",
              "No profiles in the selected sources. Ingest events or enable VoC."
            )
      );
      return;
    }
    if (!(res.items || []).length) {
      message.info(
        t(
          "abs.discover_empty",
          "No audiences matched the current identified behavior."
        )
      );
      return;
    }
    message.success(
      t("abs.discover_done", "Discovered {{count}} audience(s)", {
        count: res.items.length,
      })
    );
  };

  const onDiscover = () => {
    const hasCards = items.some((item) => isAiOrigin(item.origin));
    if (!hasCards) {
      executeDiscover(testMode);
      return;
    }
    Modal.confirm({
      title: t("abs.discover_confirm_title", "Rediscover audiences?"),
      content: t(
        "abs.discover_confirm_body",
        "Current discovered audiences will be moved to Archived. They are not permanently deleted. Rediscover can still suggest the same names again."
      ),
      okText: t("abs.discover_confirm_ok", "Rediscover"),
      onOk: () => executeDiscover(testMode),
    });
  };

  const onUploadAnalysis = async (file) => {
    setUploadingAnalysis(true);
    setUploadProgress(1);
    setUploadFileName(file?.name || "");
    setAnalysis((prev) => ({
      ...(prev || {}),
      status: "processing",
      snapshot: null,
      progress: null,
      discovered_audiences: [],
      discovery_warnings: [],
      discovery_job: { status: "pending" },
    }));
    const poll = setInterval(async () => {
      const live = await getAnalysis();
      if (!live || live.error || live.database_ready === false) return;
      if (live.status === "processing") {
        setAnalysis((prev) => ({
          ...(prev || {}),
          status: "processing",
          progress: live.progress || prev?.progress,
          filename: live.filename || file?.name,
        }));
      }
    }, 800);
    let res;
    try {
      res = await uploadAnalysisFile(file, (pct) => {
        setUploadProgress(Math.max(1, Number(pct) || 0));
      });
    } finally {
      clearInterval(poll);
      setUploadingAnalysis(false);
      setUploadProgress(0);
      setUploadFileName("");
    }
    if (applyForbidden(res)) return;

    const acceptAnalysis = async (accepted) => {
      setAnalysis(accepted);
      if (
        accepted.status === "processing" ||
        accepted.discovery_job?.status === "pending" ||
        accepted.discovery_job?.status === "running"
      ) {
        message.success(
          t("abs.explore_upload_accepted", "File accepted. Discovering audiences…")
        );
      } else {
        message.success(
          t("abs.explore_upload_done", "Analyzed {{count}} people", {
            count: accepted.snapshot?.profile_count ?? 0,
          })
        );
      }
      await load(true);
    };

    if (res && !res.error && res.database_ready !== false && res.status === "ready") {
      await acceptAnalysis(res);
      return;
    }

    const tooLarge = res?.error_code === "ABS_ANALYSIS_TOO_LARGE";
    if (!tooLarge) {
      let announced = false;
      const deadline = Date.now() + 10 * 60 * 1000;
      while (Date.now() < deadline) {
        const live = await getAnalysis();
        if (applyForbidden(live)) return;
        if (live && !live.error && live.database_ready !== false) {
          if (live.status === "ready") {
            await acceptAnalysis(live);
            return;
          }
          if (live.status === "failed") {
            res = live;
            break;
          }
          if (live.status === "processing") {
            setAnalysis((prev) => ({
              ...(prev || {}),
              status: "processing",
              progress: live.progress || prev?.progress,
              filename: live.filename || file?.name,
            }));
            if (!announced) {
              announced = true;
              message.success(
                t(
                  "abs.explore_upload_accepted",
                  "File accepted. Discovering audiences…"
                )
              );
            }
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    }

    setAnalysis({
      status: "failed",
      error: res?.error || res?.error_code,
      error_code: res?.error_code,
      snapshot: null,
    });
    message.error(
      tooLarge
        ? t(
            "abs.explore_gateway_too_large",
            "This file is too large for the upload gateway. Maximum is 25 MB."
          )
        : res?.error || t("abs.explore_upload_error", "Could not analyze this file")
    );
  };

  const onSaveSuggestion = async (suggestion) => {
    if (!suggestion?.save_eligible || !suggestion?.id) return;
    setSavingSuggestionKey(suggestion.id);
    const res = await createSegment({
      name: suggestion.name,
      description: suggestion.why,
      stable_id: suggestion.id,
      origin: "discovered",
      is_test: true,
    });
    setSavingSuggestionKey(null);
    if (applyForbidden(res)) return;
    if (!res || res.error || !res.item) {
      message.error(
        res?.error || t("abs.builder_save_error", "Could not save segment")
      );
      return;
    }
    setItems((prev) => [res.item, ...prev.filter((row) => row.id !== res.item.id)]);
    message.success(t("abs.explore_saved_done", "Audience saved"));
    setActiveTab("library");
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(t("abs.copied", "Copied"));
    } catch (_) {
      message.error(t("abs.copy_failed", "Could not copy"));
    }
  };

  const onOpenSegment = async (item) => {
    setSelectedSegment(item);
    setDrawerOpen(true);
    setMembers([]);
    setMembersTotal(item.estimated_size ?? 0);
    setMembersTruncated(false);
    setMembersLoading(true);
    const res = await listSegmentMembers(item.id);
    setMembersLoading(false);
    if (applyForbidden(res)) {
      setDrawerOpen(false);
      return;
    }
    if (!res || res.error || res.database_ready === false) {
      message.error(
        res?.error || t("abs.members_error", "Could not load audience members")
      );
      return;
    }
    setSelectedSegment(res.segment || item);
    setMembers(res.items || []);
    setMembersTotal(
      typeof res.total === "number" ? res.total : (res.items || []).length
    );
    setMembersTruncated(Boolean(res.truncated));
  };

  const onCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedSegment(null);
    setMembers([]);
    setMembersTruncated(false);
  };

  const onDownloadCsv = () => {
    downloadCsv(csvFileName(selectedSegment?.name), membersToCsv(members));
  };

  const onExportSegment = async (item) => {
    setExportingId(item.id);
    const res = await listSegmentMembers(item.id);
    setExportingId(null);
    if (applyForbidden(res)) return;
    if (!res || res.error || res.database_ready === false) {
      message.error(
        res?.error || t("abs.members_error", "Could not load audience members")
      );
      return;
    }
    const rows = res.items || [];
    if (!rows.length) {
      message.info(
        t("abs.members_empty", "No identified people match this audience.")
      );
      return;
    }
    downloadCsv(csvFileName(res.segment?.name || item.name), membersToCsv(rows));
    message.success(
      t("abs.export_done", "Exported {{count}} people", { count: rows.length })
    );
  };

  const onCopyEmails = () => {
    const emails = members.map((row) => row.email).filter(Boolean);
    if (!emails.length) {
      message.info(t("abs.members_no_emails", "No emails to copy"));
      return;
    }
    copyText(emails.join("\n"));
  };

  const openBuilderDraft = (next) => {
    setBuilderDraft({ ...next, key: Date.now() });
    setActiveTab("builder");
  };

  const onViewLibrary = () => {
    setBuilderDraft(null);
    setActiveTab("library");
  };

  const onRefineInBuilder = (item) => {
    const mapped = ruleAstToDraft(item.rule_ast);
    openBuilderDraft({
      editId: null,
      name: displaySegmentName(item.name),
      joinOperator: mapped.joinOperator,
      conditions: mapped.conditions,
      refineFromAi: true,
      mappedConditions: mapped.mapped,
    });
  };

  const onEditSegment = (item) => {
    const mapped = ruleAstToDraft(item.rule_ast);
    openBuilderDraft({
      editId: item.id,
      name: item.name,
      joinOperator: mapped.joinOperator,
      conditions: mapped.conditions,
      refineFromAi: false,
      mappedConditions: mapped.mapped,
    });
  };

  const onDuplicateSegment = async (item) => {
    const res = await createSegment({
      name: `${item.name} (copy)`.slice(0, 120),
      description: item.description || t("abs.builder_saved_desc", "Created in Segment Builder"),
      rule_ast: item.rule_ast,
      origin: "manual",
      is_test: Boolean(item.is_test),
    });
    if (applyForbidden(res)) return;
    if (!res || res.error || !res.item) {
      message.error(
        res?.error || t("abs.builder_save_error", "Could not save segment")
      );
      return;
    }
    setItems((prev) => [res.item, ...prev.filter((row) => row.id !== res.item.id)]);
    message.success(t("abs.library_duplicated", "Segment duplicated"));
  };

  const onDeleteSegment = (item) => {
    Modal.confirm({
      title: t("abs.library_delete_title", "Move to Archived?"),
      content: t(
        "abs.library_delete_body",
        "This segment will move to the Archived tab. For AI-discovered segments, Rediscover will skip this name until you restore it or permanently delete it."
      ),
      okText: t("abs.library_delete", "Archive"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await archiveSegment(item.id);
        if (applyForbidden(res)) return;
        if (!res || res.error) {
          message.error(
            res?.error || t("abs.library_delete_error", "Could not delete segment")
          );
          return;
        }
        setItems((prev) => prev.filter((row) => row.id !== item.id));
        if (res.item) {
          setArchivedItems((prev) => [res.item, ...prev.filter((row) => row.id !== item.id)]);
        }
        message.success(t("abs.library_deleted", "Segment moved to Archived"));
      },
    });
  };

  const onRestoreSegment = async (item) => {
    const res = await restoreSegment(item.id);
    if (applyForbidden(res)) return;
    if (!res || res.error || !res.item) {
      message.error(
        res?.error || t("abs.archived_restore_error", "Could not restore segment")
      );
      return;
    }
    setArchivedItems((prev) => prev.filter((row) => row.id !== item.id));
    setItems((prev) => [res.item, ...prev.filter((row) => row.id !== item.id)]);
    message.success(t("abs.archived_restored", "Segment restored"));
  };

  const onPermanentDeleteSegment = (item) => {
    Modal.confirm({
      title: t("abs.archived_delete_title", "Permanently delete this segment?"),
      content: t(
        "abs.archived_delete_body",
        "This permanently deletes the archived segment. This cannot be undone."
      ),
      okText: t("abs.archived_delete", "Permanently delete"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const res = await archiveSegment(item.id);
        if (applyForbidden(res)) return;
        if (!res || res.error) {
          message.error(
            res?.error || t("abs.archived_delete_error", "Could not delete segment")
          );
          return;
        }
        setArchivedItems((prev) => prev.filter((row) => row.id !== item.id));
        message.success(t("abs.archived_deleted", "Segment permanently deleted"));
      },
    });
  };

  if (!entitlementsReady(subscriptionStatus)) {
    return (
      <div className={styles.page}>
        <div className={styles.pageLoading} role="status" aria-live="polite">
          <Spin size="large" />
          <p>{t("abs.page_loading", "Loading Micro-Segments…")}</p>
        </div>
      </div>
    );
  }

  if (forcedLock || !hasAbsAccess(subscriptionStatus)) {
    return <AbsLockPage theme={theme} />;
  }

  const snapshot = analysis?.snapshot;
  const bootLoading = loading && analysis == null;
  const profileCount = snapshot?.profile_count ?? insights?.profile_count ?? 0;
  const eventCount = snapshot?.event_count ?? insights?.event_count ?? 0;
  const statProfiles = bootLoading && insights == null ? "—" : profileCount;
  const statEvents = bootLoading && insights == null ? "—" : eventCount;
  const memberColumns = [
    {
      title: t("abs.col_email", "Email"),
      dataIndex: "email",
      key: "email",
      ellipsis: true,
    },
    {
      title: t("abs.col_name", "Name"),
      dataIndex: "display_name",
      key: "display_name",
      ellipsis: true,
    },
    {
      title: t("abs.col_product", "Product"),
      dataIndex: "product_group",
      key: "product_group",
    },
    {
      title: t("abs.col_nps", "NPS"),
      dataIndex: "nps",
      key: "nps",
      width: 70,
    },
    {
      title: t("abs.col_sentiment", "Sentiment"),
      dataIndex: "sentiment",
      key: "sentiment",
      width: 100,
      render: (value) => (value == null ? "" : Number(value).toFixed(2)),
    },
    {
      title: t("abs.col_last", "Last event"),
      dataIndex: "last_event_at",
      key: "last_event_at",
      render: (value) =>
        value ? String(value).replace("T", " ").slice(0, 16) : "",
    },
    {
      title: t("abs.col_events", "Events"),
      dataIndex: "event_count",
      key: "event_count",
      width: 80,
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {t("abs.title", "Micro-Segments")}
          </h1>
          <p className={styles.subtitle}>
            {t(
              "abs.subtitle",
              "Explore the identified stream, then save audiences. Traits are generic; values come from this file."
            )}
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load()}
            loading={loading && analysis != null}
          >
            {t("abs.refresh", "Refresh")}
          </Button>
          {analysis?.status === "ready" || eventCount > 0 || items.length > 0 ? (
            <Button type="link" danger onClick={onClearTestData}>
              {t("abs.test_clear", "Clear file")}
            </Button>
          ) : null}
        </div>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{statProfiles}</div>
          <div className={styles.statLabel}>
            {t("abs.stat_profiles", "Identified profiles")}
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{statEvents}</div>
          <div className={styles.statLabel}>
            {t("abs.stat_events", "Events in file")}
          </div>
        </div>
      </div>

      <OrganizationContextPanel applyForbidden={applyForbidden} />

      <div className={styles.tabs} role="tablist" aria-label={t("abs.tabs", "Micro-Segments")}>
        <button
          type="button"
          role="tab"
          id="abs-tab-explore"
          aria-controls="abs-panel-explore"
          aria-selected={activeTab === "explore"}
          className={`${styles.tab} ${activeTab === "explore" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("explore")}
        >
          <Compass size={16} strokeWidth={2} />
          {t("abs.tab_explore", "Explore")}
        </button>
        <button
          type="button"
          role="tab"
          id="abs-tab-library"
          aria-controls="abs-panel-library"
          aria-selected={activeTab === "library"}
          className={`${styles.tab} ${activeTab === "library" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("library")}
        >
          <Layers size={16} strokeWidth={2} />
          {t("abs.tab_library", "Audiences")}
        </button>
        <button
          type="button"
          role="tab"
          id="abs-tab-builder"
          aria-controls="abs-panel-builder"
          aria-selected={activeTab === "builder"}
          className={`${styles.tab} ${activeTab === "builder" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("builder")}
        >
          <ListFilter size={16} strokeWidth={2} />
          {t("abs.tab_builder", "Builder")}
        </button>
        <button
          type="button"
          role="tab"
          id="abs-tab-archived"
          aria-controls="abs-panel-archived"
          aria-selected={activeTab === "archived"}
          className={`${styles.tab} ${activeTab === "archived" ? styles.tabActive : ""}`}
          onClick={() => setActiveTab("archived")}
        >
          <Archive size={16} strokeWidth={2} />
          {t("abs.tab_archived", "Archived")}
        </button>
      </div>

      {activeTab === "explore" ? (
        <div
          id="abs-panel-explore"
          role="tabpanel"
          aria-labelledby="abs-tab-explore"
          className={styles.tabPanel}
        >
          <ExplorePanel
            analysis={analysis}
            loading={bootLoading}
            refreshing={loading && analysis != null}
            onRefresh={() => load()}
            uploading={uploadingAnalysis}
            uploadProgress={uploadProgress}
            uploadFileName={uploadFileName}
            savingKey={savingSuggestionKey}
            savedNames={
              new Set(
                items.map((item) => String(item.name || "").trim().toLowerCase())
              )
            }
            liveCount={items.length}
            onUpload={onUploadAnalysis}
            onSave={onSaveSuggestion}
            onRunFromScratch={onRunFromScratch}
          />
        </div>
      ) : null}

      {activeTab === "builder" ? (
        <div
          id="abs-panel-builder"
          role="tabpanel"
          aria-labelledby="abs-tab-builder"
          className={styles.tabPanel}
        >
          <SegmentBuilder
            draft={builderDraft}
            isTest={testMode}
            onSaved={(item) => {
              setItems((prev) => [item, ...prev.filter((row) => row.id !== item.id)]);
              setBuilderDraft(null);
            }}
            onViewLibrary={onViewLibrary}
            applyForbidden={applyForbidden}
          />
        </div>
      ) : null}

      {activeTab === "library" ? (
        <div
          id="abs-panel-library"
          role="tabpanel"
          aria-labelledby="abs-tab-library"
          className={styles.tabPanel}
        >
          <div className={styles.discoverActions}>
            <Tooltip
              title={
                testMode
                  ? eventCount < 1
                    ? t(
                        "abs.discover_hint_test_empty",
                        "Upload a file on Explore first. AI discovery can then run on those events."
                      )
                    : t(
                        "abs.discover_hint_test",
                        "Re-runs AI on uploaded test events only. Current discovered cards move to Archived."
                      )
                  : t(
                      "abs.discover_hint",
                      "Re-runs AI on the selected sources. Current discovered cards move to Archived."
                    )
              }
            >
              <span>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  loading={discovering}
                  disabled={discovering || eventCount < 1}
                  onClick={onDiscover}
                >
                  {t("abs.discover", "Rediscover audiences")}
                </Button>
              </span>
            </Tooltip>
          </div>
          <SegmentLibrary
            segments={items}
            loading={loading}
            exportingId={exportingId}
            testMode={testMode}
            onOpen={onOpenSegment}
            onExport={onExportSegment}
            onRefineInBuilder={onRefineInBuilder}
            onEdit={onEditSegment}
            onDuplicate={onDuplicateSegment}
            onDelete={onDeleteSegment}
          />
        </div>
      ) : null}

      {activeTab === "archived" ? (
        <div
          id="abs-panel-archived"
          role="tabpanel"
          aria-labelledby="abs-tab-archived"
          className={styles.tabPanel}
        >
          <SegmentLibrary
            mode="archived"
            segments={archivedItems}
            loading={loading}
            exportingId={exportingId}
            testMode={testMode}
            onOpen={onOpenSegment}
            onExport={onExportSegment}
            onRestore={onRestoreSegment}
            onPermanentDelete={onPermanentDeleteSegment}
          />
        </div>
      ) : null}

      <Drawer
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        title={
          displaySegmentName(selectedSegment?.name) ||
          t("abs.members_title", "Audience")
        }
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={onCloseDrawer}
          >
            {t("common.close", "Close")}
          </button>
        }
        onClose={onCloseDrawer}
        open={drawerOpen}
        width={welcomeReviewsDrawerWidth()}
      >
        <p className={styles.drawerSubtitle}>
          {t("abs.members_size", "{{count}} people", {
            count: membersTotal,
          })}
          {` · ${t("abs.members_file_scope", "From this file")}`}
          {membersTruncated
            ? ` · ${t("abs.members_truncated", "showing first {{count}}", {
                count: members.length,
              })}`
            : ""}
        </p>
        {selectedSegment?.discovery_rationale ? (
          <p className={styles.drawerRationale}>
            {selectedSegment.discovery_rationale}
          </p>
        ) : null}
        <Space wrap className={styles.drawerActions}>
          <Button
            icon={<DownloadOutlined />}
            onClick={onDownloadCsv}
            disabled={!members.length}
          >
            {t("abs.download_csv", "Download CSV")}
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={onCopyEmails}
            disabled={!members.some((row) => row.email)}
          >
            {t("abs.copy_emails", "Copy emails")}
          </Button>
        </Space>
        <Table
          size="small"
          rowKey={(row) => row.signal_profile_id || row.email || row.gpuuid}
          loading={membersLoading}
          columns={memberColumns}
          dataSource={members}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: true }}
          locale={{
            emptyText: membersLoading
              ? t("abs.members_loading", "Loading members…")
              : t("abs.members_empty", "No identified people match this audience."),
          }}
        />
      </Drawer>
    </div>
  );
};

export default BehavioralSegmentation;
